
import React, { useState } from "react";
import { Line } from "react-chartjs-2";
import classes from "./Chart.module.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const InteractionChart = ({ data }) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const allYears = Array.from(
    new Set(data.map((item) => new Date(item.date).getFullYear()))
  ).sort((a, b) => b - a);
  
  const defaultYears = allYears.slice(0, 3);
  const [selectedYears, setSelectedYears] = useState(defaultYears);


  const initialColorMap = {
    2024: "rgba(255, 89, 0, 0.6)",
    2023: "rgba(34, 132, 143, 0.6)",
    2022: "rgba(100, 149, 237, 0.6)",
  };


  const [yearColors, setYearColors] = useState(initialColorMap);

  const assignColorToYear = (year) => {
 
    if (yearColors[year]) return yearColors[year];

   
    const newColor = `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(
      Math.random() * 255
    )}, ${Math.floor(Math.random() * 255)}, 0.6)`;

    setYearColors((prevColors) => ({
      ...prevColors,
      [year]: newColor,
    }));
    return newColor;
  };

  const interactionData = selectedYears.reduce((acc, year) => {
    acc[year] = {
      monthly: Array(12).fill(0),
      cumulative: Array(12).fill(0),
    };

    data.forEach((item) => {
      const itemYear = new Date(item.date).getFullYear();
      const month = new Date(item.date).getMonth();

      if (itemYear === year) acc[year].monthly[month] += 1;
    });

    acc[year].cumulative[0] = acc[year].monthly[0];
    for (let i = 1; i < 12; i++) {
      acc[year].cumulative[i] =
        acc[year].cumulative[i - 1] + acc[year].monthly[i];
    }

    return acc;
  }, {});

  console.log("mappped data ", interactionData)

  const getDataSets = () =>
    selectedYears.map((year) => ({
      label: `Year ${year}`,
      data: interactionData[year].cumulative,
      borderColor: assignColorToYear(year),
      backgroundColor: assignColorToYear(year),
      fill: false,
    }));

  const chartData = {
    labels: months,
    datasets: getDataSets(),
  };

  const options = {
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: "Cumulative Interaction Count" },
      },
      x: {
        title: { display: true, text: "Months" },
      },
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "Cumulative Interactions by Month for Selected Years",
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const year = context.dataset.label.split(" ")[1];
            const monthIndex = context.dataIndex;
            const monthlyCount = interactionData[year].monthly[monthIndex];
            const cumulativeCount = context.raw;
            return [
              `Year ${year}:`,
              `  Cumulative: ${cumulativeCount}`,
              `  Monthly Count: ${monthlyCount}`,
            ];
          },
        },
      },
      datalabels: {
        display: true,
        color: "black",
        align: "top",
        font: { weight: "bold" },
        formatter: (value, context) => {
          const year = context.dataset.label.split(" ")[1];
          const monthIndex = context.dataIndex;
          return interactionData[year].monthly[monthIndex];
        },
      },
    },
  };

  const handleYearSelection = (year) => {
    setSelectedYears((prevYears) =>
      prevYears.includes(year)
        ? prevYears.filter((y) => y !== year)
        : [...prevYears, year]
    );
  };

  return (
    <div className={classes.interactionChart}>
      <h2>
        <span className={classes.interactionText}>Interaction</span>
        <span className={classes.chartText}> Chart</span>
        <span
          className={classes.infoButton}
          onMouseOver={() => setIsTooltipVisible(true)}
          onMouseOut={() => setIsTooltipVisible(false)}
        >
          <QuestionMarkIcon
            style={{
              fontSize: "18px",
              backgroundColor: "#F3F8FF",
              color: "#3498db",
              borderRadius: "50%",
              padding: "2px",
              marginLeft: "1rem",
            }}
          />
          {isTooltipVisible && (
            <div className={classes.infoCard}>
              <h6>Interaction Chart</h6>
              <p style={{ color: " #838194" }}>
                Interaction Chart enables you to compare interactions year over
                year. This works as a benchmark to identify actions to take when
                compared to previous years. If interaction count for the current
                year is better than previous years then it reflects better
                engagement. If the interactions are below previous years then it
                is time to identify opportunities to engage. Create a Quarterly
                Action Plan in collaboration with leadership to increase
                engagement.
              </p>
            </div>
          )}
        </span>
      </h2>
      <Line data={chartData} options={options} />
      <br />
      <div className={classes.centeredContainer}>
        <h3>
          <span style={{ color: "rgb(255, 89, 0)" }}>Select</span>
          <span style={{ color: "#22848f" }}> Years to Display</span>
        </h3>
        <div className={classes.buttonContainer}>
          {allYears.map((year) => (
            <button
              key={year}
              onClick={() => handleYearSelection(year)}
              style={{
                backgroundColor: selectedYears.includes(year)
                  ? assignColorToYear(year)
                  : "",
              }}
              className={classes.button}
            >
              {year}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InteractionChart;
